import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2, AlertCircle } from "lucide-react";

interface QualificationStepProps {
  framework: 'BANT' | 'MEDDIC';
  onFrameworkChange: (framework: 'BANT' | 'MEDDIC') => void;
  isQualifying: boolean;
  onRunQualification: () => void;
  qualificationResults: Record<string, number> | null;
  selectedLeadCount: number;
  error?: string;
}

export function QualificationStep({
  framework,
  onFrameworkChange,
  isQualifying,
  onRunQualification,
  qualificationResults,
  selectedLeadCount,
  error
}: QualificationStepProps) {
  const frameworks = {
    BANT: ["Budget", "Authority", "Need", "Timeline"],
    MEDDIC: ["Metrics", "Economic Buyer", "Decision Criteria", "Decision Process", "Identify Pain", "Champion"]
  };

  return (
    <div className="space-y-4">
      <p className="text-muted-foreground">
        AI will analyze conversations and apply qualification scoring to {selectedLeadCount} leads.
      </p>
      
      {error && (
        <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">{error}</span>
        </div>
      )}
      
      <div className="space-y-3">
        <Label>Select Qualification Framework</Label>
        <RadioGroup value={framework} onValueChange={(v) => onFrameworkChange(v as 'BANT' | 'MEDDIC')}>
          {Object.entries(frameworks).map(([key, items]) => (
            <Card key={key} className={framework === key ? 'border-primary' : ''}>
              <CardHeader className="p-4 pb-2">
                <div className="flex items-center gap-3">
                  <RadioGroupItem value={key} id={key} />
                  <Label htmlFor={key} className="cursor-pointer">
                    <CardTitle className="text-base">{key} Framework</CardTitle>
                  </Label>
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="flex flex-wrap gap-2">
                  {items.map(item => (
                    <Badge key={item} variant="secondary">{item}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </RadioGroup>
      </div>
      
      <Button 
        onClick={onRunQualification} 
        disabled={isQualifying || selectedLeadCount === 0}
        className="w-full"
      >
        {isQualifying ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Qualifying Leads...
          </>
        ) : (
          <>
            <Sparkles className="h-4 w-4 mr-2" />
            Run AI Qualification
          </>
        )}
      </Button>
      
      {qualificationResults && (
        <Card className="bg-primary/5 border-primary/30">
          <CardContent className="p-4">
            <div className="text-sm font-medium mb-2">Qualification Results</div>
            <div className="grid grid-cols-2 gap-2">
              <div className="text-center p-2 bg-green-500/10 rounded">
                <div className="text-lg font-bold text-green-500">{qualificationResults.qualified}</div>
                <div className="text-xs text-muted-foreground">Qualified</div>
              </div>
              <div className="text-center p-2 bg-amber-500/10 rounded">
                <div className="text-lg font-bold text-amber-500">{qualificationResults.needsWork}</div>
                <div className="text-xs text-muted-foreground">Needs Nurturing</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
